import { createFileRoute } from '@tanstack/react-router'
import { Alert, Button, Form, Input, Modal, Space, Spin, Table } from 'antd'
import { useState } from 'react'
import {
  useBatches,
  useCreateOrUpdateBatch,
  useDeleteBatch,
} from '../hooks/useBatches'
import { getPagination } from '../components/TablePagination'
import type { ColumnsType } from 'antd/es/table'
import type { BatchData } from '../services/batchService'

export const Route = createFileRoute('/batch')({
  component: BatchPage,
})

function BatchPage() {
  // 使用 React Query hooks 管理数据
  const { data: batches, isLoading, error } = useBatches()
  const createOrUpdateMutation = useCreateOrUpdateBatch()
  const deleteMutation = useDeleteBatch()

  // 本地状态管理
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentBatch, setCurrentBatch] = useState<BatchData | null>(null)
  const [form] = Form.useForm()

  // 分页状态
  const [pageNum, setPageNum] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 表格列定义
  const columns: ColumnsType<BatchData> = [
    { title: '考试项目 ID', dataIndex: 'id', key: 'id' },
    { title: '考试项目名称', dataIndex: 'name', key: 'name' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button
            size="small"
            danger
            onClick={() => handleDelete(record.id)}
            loading={deleteMutation.isPending}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ]

  // 处理新增
  const handleAdd = () => {
    setCurrentBatch(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  // 处理编辑
  function handleEdit(batch: BatchData) {
    setCurrentBatch(batch)
    form.setFieldsValue({ name: batch.name })
    setIsModalOpen(true)
  }

  // 处理提交（新增或修改）
  const handleSubmit = () => {
    form.validateFields().then(({ name }) => {
      const requestData = {
        name,
        ...(currentBatch && { id: currentBatch.id }),
      }

      createOrUpdateMutation.mutate(requestData, {
        onSuccess: () => {
          setIsModalOpen(false)
          form.resetFields()
          setCurrentBatch(null)
        },
      })
    })
  }

  // 处理删除
  function handleDelete(id: number) {
    Modal.confirm({
      title: '确认删除',
      content: (
        <div>
          <p>确定要删除该考试项目吗？</p>
          <Alert
            message="警告"
            description="删除该考试项目将同步删除其下的所有职位代码数据，且无法恢复。"
            type="warning"
            showIcon
            className="mt-2"
          />
        </div>
      ),
      onOk: () => {
        deleteMutation.mutate(id)
      },
    })
  }

  // 处理分页变化
  const handlePaginationChange = (page: number, pageSize: number) => {
    setPageNum(page)
    setPageSize(pageSize)
  }

  // 处理取消
  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
    setCurrentBatch(null)
  }

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className="p-4">
        <Alert
          message="加载失败"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              重试
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">考试项目管理</h2>
        <Button type="primary" onClick={handleAdd}>
          新增考试项目
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={batches}
        rowKey="id"
        bordered
        loading={isLoading}
        pagination={getPagination(
          pageNum,
          pageSize,
          batches?.length || 0,
          handlePaginationChange,
        )}
      />

      <Modal
        title={currentBatch ? '编辑考试项目' : '新增考试项目'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={600}
        confirmLoading={createOrUpdateMutation.isPending}
      >
        <Form form={form} layout="vertical" className="mt-6">
          <Form.Item
            label="考试项目名称"
            name="name"
            rules={[{ required: true, message: '请输入考试项目名称' }]}
          >
            <Input placeholder="请输入考试项目名称" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
